import React, { useState } from 'react';
import { Card, CardBody, Button, Input, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { formatCurrency } from '../../../../utils/format';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { getMethodIcon } from '../constants';

const SalesCartPanel = ({ 
    cartItems, 
    payments, 
    totals, 
    onRemoveItem, 
    onRemovePayment, 
    onProceed,
    // Props de Matrícula Pós-Venda
    pendingEnrollments = {}, 
    onOpenEnrollment = null, 
    onClearEnrollment = null
}) => {
    const [dueDate, setDueDate] = useState('');
    const [showModal, setShowModal] = useState(false);

    // Cálculos de Totais (Usa totals do hook se disponível, senão recalcula)
    const subtotal = totals?.subtotal ?? cartItems.reduce((acc, curr) => acc + (parseFloat(curr.price) || 0), 0);
    const totalPaid = totals?.totalPaid ?? payments.reduce((acc, curr) => acc + (parseFloat(curr.value) || 0), 0);
    const balance = totals?.balance ?? (subtotal - totalPaid);

    const handleInitialFinalize = () => {
        if (balance > 0 && !dueDate) {
            setShowModal(true);
        } else {
            handleConfirmFinalize();
        }
    };

    const handleConfirmDueDate = () => {
        setShowModal(false);
        // Apenas salva a data e fecha o modal. A venda NÃO é finalizada aqui.
    };

    const handleConfirmFinalize = () => {
        setShowModal(false);
        onProceed({
            balancePending: balance > 0,
            dueDate: balance > 0 ? dueDate : null,
            totalItems: subtotal,
            totalPaid: totalPaid
        });
    };

    return (
        <React.Fragment>
            <Card className="shadow-sm border-0" style={{ marginTop: '2px' }}>
                <CardBody className="d-flex flex-column">
                    <h5 className="font-size-15 fw-bold text-dark mb-4">Resumo da Venda</h5>

                    <div className="flex-grow-1">
                        {/* LISTA DE ITENS */}
                        <div className="mb-4">
                            <label className="text-muted font-size-12 fw-bold text-uppercase mb-2 d-block">Itens da venda</label>
                            {cartItems.length === 0 ? (
                                <div className="text-center py-3 border rounded border-light bg-light opacity-50 mb-3">
                                    <span className="text-muted font-size-12 italic">Nenhum item selecionado</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2 mb-3">
                                    {cartItems.map((item) => (
                                        <div key={item.cartId} className="p-2 border rounded bg-white shadow-sm animate__animated animate__fadeIn mb-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <span className="fw-bold text-dark font-size-13 d-block pe-2 pb-1">{item.name}</span>
                                                </div>
                                                <div className="text-end d-flex align-items-center">
                                                    <span className="fw-bold text-dark me-2">{formatCurrency(item.price || item.unitPrice)}</span>
                                                    <Button color="link" size="sm" className="p-0 text-danger" onClick={() => onRemoveItem(item.cartId)}>
                                                        <i className="mdi mdi-close-circle-outline font-size-16"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* LISTA DE PAGAMENTOS */}
                        <div className="mb-4">
                            <label className="text-muted font-size-12 fw-bold text-uppercase mb-2 d-block">Pagamentos Realizados</label>
                            {payments.length === 0 ? (
                                <div className="text-center py-3 border rounded border-light bg-light opacity-50">
                                    <span className="text-muted font-size-12 italic">Nenhum pagamento registrado</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {payments.map((p) => (
                                        <div key={p.id} className="p-2 border rounded bg-light-subtle d-flex justify-content-between align-items-center animate__animated animate__fadeIn">
                                            <div className="d-flex align-items-center">
                                                <i className={`mdi ${getMethodIcon(p.methodId)} text-primary me-2 font-size-18`}></i>
                                                <div>
                                                    <span className="fw-bold text-dark font-size-13 d-block">{p.methodLabel}</span>
                                                    <small className="text-muted">
                                                        {[PAYMENT_METHODS.CREDIT_CARD, PAYMENT_METHODS.DEBIT_CARD].includes(p.methodId)
                                                            ? `${p.brand} - ${p.installments}`
                                                            : 'À vista'}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="text-end d-flex align-items-center">
                                                <span className="fw-bold text-success me-2">{formatCurrency(p.value)}</span>
                                                <Button color="link" size="sm" className="p-0 text-danger" onClick={() => onRemovePayment(p.id)}>
                                                    <i className="mdi mdi-trash-can-outline font-size-16"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RESUMO FINANCEIRO */}
                    <div className="cart-summary mt-auto pt-3 border-top border-2">
                        <div className="d-flex justify-content-between mb-1 text-muted font-size-13">
                            <span>Subtotal</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>

                        {totals?.discount > 0 && (
                            <div className="d-flex justify-content-between mb-1 text-danger font-size-13">
                                <span>Desconto</span>
                                <span>- {formatCurrency(totals.discount)}</span>
                            </div>
                        )}

                        <div className="d-flex justify-content-between mb-1 text-muted font-size-13">
                            <span>Total Pago</span>
                            <span className="text-success fw-bold">{formatCurrency(totalPaid)}</span>
                        </div>

                        <div className={`d-flex justify-content-between mt-2 pt-2 border-top ${balance > 0 ? 'text-danger' : totals?.surplus > 0 ? 'text-warning' : 'text-success'}`}>
                            <span className="fw-bold font-size-16">
                                {balance > 0 ? 'Saldo Restante' : totals?.surplus > 0 ? 'Excesso (Troco)' : 'Status'}
                            </span>
                            <span className="fw-bold font-size-18">
                                {balance > 0
                                    ? formatCurrency(balance)
                                    : totals?.surplus > 0
                                        ? formatCurrency(totals.surplus)
                                        : 'QUITADO'
                                }
                            </span>
                        </div>
                        
                        {/* Exibe botão/área de previsão caso haja saldo devedor */}
                        {balance > 0 && dueDate && (
                            <div className="mt-2 text-end">
                                <div className="d-flex align-items-center justify-content-between p-2 mt-2 bg-warning-subtle rounded border border-warning">
                                    <div>
                                        <span className="d-block text-warning fw-bold font-size-11 text-start">PREVISÃO DE RECEBIMENTO</span>
                                        <span className="d-block text-dark font-size-13">
                                            {dueDate.split('-').reverse().join('/')}
                                        </span>
                                    </div>
                                    <Button color="link" className="p-0 text-warning" onClick={() => setShowModal(true)}>
                                        <i className="mdi mdi-pencil" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        <Button
                            color="success"
                            size="lg"
                            block
                            className="w-100 fw-bold mt-4 py-2 shadow-sm text-uppercase"
                            onClick={handleInitialFinalize}
                            disabled={cartItems.length === 0 || (subtotal === 0 && payments.length === 0) || cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0))}
                        >
                            <i className={`mdi ${cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0)) ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline'} me-2`}></i>
                            {cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0)) ? 'PENDENTE: TURMAS' : 'FINALIZAR VENDA'}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* MODAL DE CONFIRMAÇÃO DE SALDO DEVEDOR */}
            <Modal isOpen={showModal} toggle={() => setShowModal(!showModal)} centered backdrop="static" className="border-0 shadow">
                <ModalHeader toggle={() => setShowModal(false)} className="border-bottom pb-3">
                    <span className="fw-bold text-dark font-size-15">
                        <i className="mdi mdi-alert-circle-outline text-warning me-2"></i>
                        Agendar Recebimento do Restante
                    </span>
                </ModalHeader>
                <ModalBody className="p-4">
                    <p className="font-size-14 text-muted mb-4 text-center">
                        A venda ficará com um <strong>saldo restante de {formatCurrency(balance)}</strong>. 
                        Qual a data prevista para o cliente realizar o pagamento?
                    </p>
                    <div className="border p-3 rounded">
                        <label className="text-muted font-size-12 fw-bold text-uppercase mb-2 d-block">
                            Data prevista
                        </label>
                        <InputGroup size="sm">
                            <InputGroupText className="bg-white border-end-0">
                                <i className="mdi mdi-calendar text-primary"></i>
                            </InputGroupText>
                            <Input
                                type="date"
                                className="border-start-0 ps-0"
                                value={dueDate || ''}
                                onChange={(e) => setDueDate(e.target.value)}
                            />
                        </InputGroup>
                    </div>
                </ModalBody>
                <ModalFooter className="border-top pt-3">
                    <Button color="light" className="text-muted" onClick={() => setShowModal(false)}>Cancelar</Button>
                    <Button 
                        color="primary" 
                        onClick={handleConfirmDueDate} 
                        disabled={!dueDate}
                    >
                        Confirmar Data
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default SalesCartPanel;
